import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Input, Stack, Typography } from '@mui/material';
import React from 'react';
import { DialogPromptSelectAvatar } from '../../api/DialogPromptApi';
import { IUser, UserApi } from '../../api/UserApi';
import AppBarContext from '../../context/AppBarContext';
import ChatBubble from '../ChatBubble';
import { handleErrorMessage } from '../ErrorDialog';
import { getAudioByText } from './DialogPromptComponent';

import { useHistory, useLocation } from 'react-router-dom';
import female_shilhouette from '../../asset/female_silhouette.png';
import male_shilhouette from '../../asset/male_silhouette.png';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import { scrollElementIntoViewWithFallback } from '../../util/scrollUtils';

interface DialogPromptSelectAvatarComponentProps {
    dialogPrompt: DialogPromptSelectAvatar;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

const DialogPromptSelectAvatarComponent: React.FC<DialogPromptSelectAvatarComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef, isSettings = false }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);

    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [fileSizeError, setFileSizeError] = React.useState(false);

    const [studentAvatar, setStudentAvatar] = React.useState<string | null>(null);
    const avatarOptions = React.useMemo(() => [male_shilhouette, female_shilhouette], []);
    const avatarRef = React.useRef<HTMLDivElement>(null);

    const [chatBubbleDone, setChatBubbleDone] = React.useState(false);

    const [isUploading, setIsUploading] = React.useState(false);
    const [isConfirming, setIsConfirming] = React.useState(false);
    const [confirmEnabled, setConfirmEnabled] = React.useState(false);

    const handleAvatarClick = (avatar: string) => {
        localStorage.setItem("studentAvatar", avatar);
        setStudentAvatar(avatar);
    };

    const handleFileUpload = () => {
        if (fileInputRef.current && fileInputRef.current.files && fileInputRef.current.files.length > 0) {
            setIsUploading(true);
            const file = fileInputRef.current.files[0];
            const reader = new FileReader();

            reader.onload = (event) => {
                try {
                    if (event.target) {
                        const fileData = event.target.result; // This can be a Blob or data URL

                        if (fileData) {
                            const fileSizeInBytes = file.size;
                            const maxSizeInBytes = 3 * 1024 * 1024; // Maximum file size in bytes (e.g., 128k)

                            if (fileSizeInBytes > maxSizeInBytes) {
                                setFileSizeError(true); // Set the error state to display the dialog
                            } else {
                                // Save the file data in local storage
                                const avatarFileData = fileData.toString()
                                localStorage.setItem('studentAvatar', avatarFileData);
                                setStudentAvatar(avatarFileData);
                            }
                        }
                    }
                } finally {
                    setIsUploading(false); // ✅ always runs
                }
            }

            reader.readAsDataURL(file); // Read the file data            
        }
    };

    const handleCloseDialog = () => {
        setFileSizeError(false);
    };

    const handleConfirm = React.useCallback(() => {
        setIsConfirming(true); // disable + show spinner

        const selectedAvatar = localStorage.getItem("studentAvatar"); // Get the latest value
        // console.log("Confirm clicked, current avatar:", selectedAvatar); // Debugging

        if (selectedAvatar) {
            // Update user in database
            const updatedUser: IUser = {
                avatar: selectedAvatar,
            };
            // Call the update function
            UserApi.update(updatedUser)
                .then(() => {
                    setDialogPromptComplete(dialogPrompt.id);
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                })
                .finally(() => {
                    setIsConfirming(false);
                })
        }
    }, [setIsConfirming, setDialogPromptComplete, dialogPrompt.id, setErrorMessage, history, location]);

    React.useEffect(() => {
        clearFooterList();
        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio, isSettings)
    }, [clearFooterList, dialogPrompt.prompt, history, isSettings, setErrorMessage]);

    React.useEffect(() => {
        if (studentAvatar && studentAvatar.trim().length > 0) {
            setConfirmEnabled(true);
        }
    }, [studentAvatar])

    React.useEffect(() => {
        if (chatBubbleDone) {
            setActionList([
                ...avatarOptions.map((src) => (
                    <Box
                        key={src}
                        component="img"
                        src={src}
                        alt="Avatar"
                        sx={{
                            p: 0,
                            maxHeight: 56,  // matches button height below
                            height: '100%',
                            width: 'auto',
                            borderRadius: "50%",
                            cursor: "pointer",
                            border: studentAvatar === src ? "3px solid blue" : "3px solid transparent",
                        }}
                        onClick={() => handleAvatarClick(src)}
                    />
                )),
                <>
                    <Input
                        type="file"
                        inputRef={fileInputRef}
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                        id="file-upload"
                        inputProps={{ accept: 'image/*' }}
                    />
                    <label htmlFor="file-upload">
                        <Button variant="contained" component="span" disabled={isUploading}>
                            {isUploading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Upload Avatar'
                            )}
                        </Button>
                    </label></>
                ,
                <Button variant="contained" onClick={handleConfirm}
                    disabled={(function () {
                        const result = isConfirming || !confirmEnabled;
                        return result;
                    })()}>
                    {isConfirming ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : (
                        'Confirm'
                    )}
                </Button>
            ])
        }
    }, [chatBubbleDone, isConfirming, confirmEnabled, studentAvatar, isUploading, avatarOptions, handleConfirm, setActionList]);

    // console.log("isConfirming:", isConfirming, "confirmEnabled:", confirmEnabled);
    // console.log("disabled evaluated to:", isConfirming || !confirmEnabled);

    return (
        <Stack>
            <ChatBubble
                isTeacher={dialogPrompt.is_teacher}
                message={dialogPrompt.prompt}
                messageAudio={promptAudio ? promptAudio : null}
                showAvatar={showAvatar}
                setChatBubbleDone={setChatBubbleDone}
                chatBubbleContainerRef={chatBubbleContainerRef}
            />
            {studentAvatar == null ? null : (
                <div ref={avatarRef} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <img
                        src={studentAvatar || ''}
                        style={{ maxHeight: '4em', maxWidth: '4em' }}
                        onLoad={() => {
                            if (avatarRef.current) {
                                scrollElementIntoViewWithFallback(avatarRef.current);
                            }
                        }}
                    />
                </div>
            )}
            <Dialog open={fileSizeError} onClose={handleCloseDialog}>
                <DialogTitle><Typography variant="h4" sx={{ color: '#383838' }}>Error</Typography></DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: '#8D8D8D' }}>The file size exceeds the maximum allowed size of 3MB.</Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', '& .MuiButton-root': { flex: 1 } }}>
                    <Button onClick={handleCloseDialog} sx={{ color: '#787878' }}>OK</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default DialogPromptSelectAvatarComponent;
